import {
  faCheck,
  faHexagonExclamation,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import AdminPage from '../components/AdminPage';
import Button from '../components/Button';
import Heading from '../components/Heading';
import List from '../components/List';
import { useConfirm } from '../hooks/usePrompt';
import { useToken } from '../hooks/useToken';
import SurveyOverview from './components/SurveyOverview';
import DownloadReport from './components/DownloadReport';
import { useCloseQuizMutation } from './mutations/closeQuizMutation';
import { useReopenQuizMutation } from './mutations/reopenQuizMutation';
import { useGetQuizQuery } from './queries/getQuizQuery';
import { checkUserRole } from '../../../services/user';
import { useResendCompletionEmailMutation } from './mutations/resendCompletionEmailMutation';
import QuizResponseEditDialog from './components/QuizResponseEditDialog';
import { useUpdateQuizResponseMutation } from './hooks/useUpdateQuizResponseMutation';
import SendEmailConfirmationModal from './components/SendEmailConfirmationModal';
import { useDeleteQuizResponseMutation } from './mutations/deleteQuizResponseMutation';

type Params = {
  readonly id: string;
};

export function AdminSurveyView() {
  const [token] = useToken();

  const { id } = useParams<Params>();
  const { data, refetch } = useGetQuizQuery({ variables: { id: id! } });
  const userRole = checkUserRole();
  const [resendCompletionEmail, { loading: resendLoading }] =
    useResendCompletionEmailMutation();
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [responseToSendEmail, setResponseToSendEmail] = useState<any>(null);

  const [approvalFilter, setApprovalFilter] = useState<
    'all' | 'approved' | 'not-approved'
  >('all');
  const [selectedResponse, setSelectedResponse] = useState<any>(null);
  const [saveIsLoading, setSaveIsLoading] = useState(false);

  const confirm = useConfirm();
  const [updateQuizResponse, { loading: isUpdating }] =
    useUpdateQuizResponseMutation();

  const [closeQuiz, { loading: closeQuizLoading }] = useCloseQuizMutation({
    variables: {
      id: id!,
    },
    onCompleted: () => refetch(),
  });

  const [reopenQuiz, { loading: reopenQuizLoading }] = useReopenQuizMutation({
    variables: {
      id: id!,
    },
    onCompleted: () => refetch(),
  });

  const [deleteQuizResponse, { loading: deleteLoading }] =
    useDeleteQuizResponseMutation({
      onCompleted: () => refetch(),
    });

  const reportLink = useMemo(() => {
    return `${process.env.REACT_APP_GRAPHQL_ENDPOINT}/report/${id}?token=${token}`;
  }, [id, token]);

  if (!data) {
    return null;
  }
  const sortedResponses = [...data.quiz.responses]
    .sort((a, b) => {
      if (a.respondentType === 'LEADER' && b.respondentType !== 'LEADER') {
        return -1;
      }
      if (a.respondentType !== 'LEADER' && b.respondentType === 'LEADER') {
        return 1;
      }
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    })
    .map((response) => {
      let emailStatus = null;

      if (response?.completitionEmailSentStatus) {
        try {
          const parsedStatus = JSON.parse(response.completitionEmailSentStatus);
          emailStatus = parsedStatus?.Messages?.[0]?.Status || null;
        } catch (e) {
          console.error('Error parsing JSON:', e);
          emailStatus = null;
        }
      }

      return {
        ...response,
        emailStatus,
      };
    });
  const filteredResponses = sortedResponses.filter((response) => {
    if (approvalFilter === 'approved')
      return response.isReportApproved === true;
    if (approvalFilter === 'not-approved')
      return response.isReportApproved === false;
    return true;
  });

  function openApprovalModal(response: any) {
    setSelectedResponse(response);
  }

  return (
    <AdminPage title={`Survey - ${data.quiz.title}`}>
      {data.quiz.closed && (
        <div className="text-red-600 flex items-center space-x-2">
          <FontAwesomeIcon icon={faHexagonExclamation} />
          <p>This survey is closed and not accessible for completion.</p>
        </div>
      )}

      <SurveyOverview
        survey={data.quiz}
        userRole={userRole}
        reportLink={reportLink}
        actions={
          data.quiz.closed ? (
            <Button
              onClick={() => confirm('Reopen the survey?', () => reopenQuiz())}
            >
              Reopen survey
            </Button>
          ) : (
            <Button
              theme="danger"
              onClick={() =>
                confirm(
                  'Close the survey? Results will remain accessible.',
                  () => closeQuiz(),
                )
              }
            >
              Close survey
            </Button>
          )
        }
        refetch={refetch}
      />

      <Heading
        level={2}
        text="Survey responses"
        actions={
          <div className="flex gap-10 flex-row">
            <select
              className="border border-gray-300 rounded p-1"
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value as any)}
            >
              <option value="all">All</option>
              <option value="approved">Approved</option>
              <option value="not-approved">Not approved</option>
            </select>
          </div>
        }
      />
      {/* Nagłówki kolumn */}
      <div className="grid grid-cols-[180px_100px_130px_70px_130px_150px_130px_50px] gap-3 font-semibold text-sm text-gray-700 mb-2 items-center">
        <p>Participant Name</p>
        <p>Type</p>
        <p>Date completed</p>
        <p>Approval</p>
        <p>Email sent</p>
        <p>Report</p>
        <p>Email</p>
        <p></p>
      </div>

      {/* Lista odpowiedzi */}
      <List
        placeholder="No responses yet"
        items={filteredResponses}
        render={(response) => (
          <div className="grid grid-cols-[180px_100px_130px_70px_130px_150px_130px_50px] gap-3 text-xs text-gray-600 items-center leading-tight">
            <p className="text-nowrap overflow-hidden text-ellipsis">
              {response.respondentName ?? 'Anonymous'}
            </p>

            <p>{response.respondentType}</p>

            <p>{format(response.created, 'do MMM yyyy')}</p>

            <p>
              <button onClick={() => openApprovalModal(response)}>
                {response.isReportApproved ? (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="bg-green-500 text-white p-2 rounded text-xl"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faHexagonExclamation}
                    className="bg-yellow-500 text-white p-2 rounded text-xl"
                  />
                )}
              </button>
            </p>

            <p>
              {response.completitionEmailSentAt && (
                <div
                  className={
                    response.emailStatus === 'success'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }
                  title={
                    response.emailStatus === 'success'
                      ? ''
                      : (response.completitionEmailSentStatus ?? '')
                  }
                >
                  {format(
                    new Date(response.completitionEmailSentAt),
                    'do MMM yyyy HH:mm',
                  )}
                </div>
              )}
            </p>

            <p>
              <DownloadReport response={response} />
            </p>

            <p>
              {response.isReportApproved ? (
                response.completitionEmailSentStatus === 'resend' ? (
                  <div className="text-yellow-600">Send requested</div>
                ) : (
                  <Button
                    loading={resendLoading}
                    onClick={() => {
                      setResponseToSendEmail(response);
                      setShowSendEmailModal(true);
                    }}
                  >
                    {response.completitionEmailSentAt ? 'Resend' : 'Send'} email
                  </Button>
                )
              ) : (
                <span className="text-gray-400 italic">
                  Report not approved
                </span>
              )}
            </p>

            <p>
              {userRole === 'Admin' && (
                <button
                  disabled={deleteLoading}
                  onClick={() =>
                    confirm(
                      'Are you sure you want to delete this response? This action cannot be undone.',
                      () =>
                        deleteQuizResponse({
                          variables: { id: response.id },
                        }),
                    )
                  }
                  className="text-red-600 hover:text-red-800 disabled:opacity-50"
                  title="Delete response"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-lg" />
                </button>
              )}
            </p>
          </div>
        )}
      />

      {selectedResponse && (
        <QuizResponseEditDialog
          open={!!selectedResponse}
          isLoading={isUpdating || saveIsLoading}
          initialData={{
            aiReportData: selectedResponse.aiReportData || {
              mainProfile: '',
              foreignTraits: [],
              dominantTraits: [],
            },
            reportUrl: selectedResponse.reportUrl || '',
            isReportApproved: selectedResponse.isReportApproved || false,
          }}
          onClose={() => setSelectedResponse(null)}
          onSave={async (updated) => {
            if (!selectedResponse?.id) return;

            try {
              await updateQuizResponse({
                variables: {
                  id: selectedResponse.id,
                  input: updated,
                },
              });

              const refreshed = await refetch();
              const updatedResponse = refreshed.data?.quiz.responses.find(
                (r: any) => r.id === selectedResponse.id,
              );

              if (updatedResponse) {
                setSelectedResponse(updatedResponse);
              }
            } catch (err) {
              console.error('Error saving response:', err);
            }
          }}
        />
      )}
      {showSendEmailModal && responseToSendEmail && (
        <SendEmailConfirmationModal
          open={showSendEmailModal}
          onClose={() => {
            setShowSendEmailModal(false);
            setResponseToSendEmail(null);
          }}
          onConfirm={() => {
            resendCompletionEmail({
              variables: { responseId: responseToSendEmail.id },
              onCompleted: () => {
                refetch();
                setShowSendEmailModal(false);
                setResponseToSendEmail(null);
              },
            });
          }}
        />
      )}

      <Outlet />
    </AdminPage>
  );
}
